import React from 'react';
import ReviewImage from '../common/ReviewImage';
import { additiveAmountText, additivesText, CHANNEL_LABELS, polymersText, solventsText, stockLabel } from '../DataSubmission/solutionText';

const formatKey = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());

function withUnit(value, unit) {
    if (value === null || value === undefined || value === '') return null;
    return unit ? `${value} ${unit}` : `${value}`;
}

// Turns a DTO + a list of {key, label, unitKey?, formatter?} specs into
// display-ready {label, value} pairs, dropping anything not actually recorded
// so a section only shows fields that have data instead of a wall of "N/A".
function buildFields(obj, specs) {
    if (!obj) return [];
    return specs
        .map(({ key, label, unitKey, formatter }) => {
            const raw = obj[key];
            if (raw === null || raw === undefined || raw === '') return null;
            let display;
            if (formatter) display = formatter(raw, obj);
            else if (unitKey) display = withUnit(raw, obj[unitKey]);
            else if (typeof raw === 'boolean') display = raw ? 'Yes' : 'No';
            else display = String(raw);
            if (display === null || display === '') return null;
            return { label, value: display };
        })
        .filter(Boolean);
}

const FieldGrid = ({ fields }) => {
    if (!fields || fields.length === 0) return null;
    return (
        <div className="record-detail-field-grid">
            {fields.map((f) => (
                <div key={f.label} className="record-detail-field">
                    <span className="record-detail-field-label">{f.label}</span>
                    <span className="record-detail-field-value">{f.value}</span>
                </div>
            ))}
        </div>
    );
};

const BadgeList = ({ items, className = 'bg-secondary-subtle text-dark border' }) => {
    if (!items || items.length === 0) return null;
    return (
        <div className="record-detail-badge-list">
            {items.map((item, i) => (
                <span key={i} className={`badge ${className}`}>{item}</span>
            ))}
        </div>
    );
};

const MiniTable = ({ columns, rows }) => {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="details-table-wrapper">
            <table className="inner-detail-table">
                <thead>
                    <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i}>{row.map((cell, j) => <td key={j}>{cell ?? '-'}</td>)}</tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const DefinitionMap = ({ map }) => {
    if (!map || Object.keys(map).length === 0) return null;
    const fields = Object.entries(map)
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => ({ label: formatKey(k), value: String(v) }));
    return <FieldGrid fields={fields} />;
};

const Section = ({ icon, title, children }) => (
    <div className="dashboard-section record-detail-card">
        <div className="section-header">
            <h3><i className={`bi ${icon}`}></i> {title}</h3>
        </div>
        <div className="record-detail-card-body">
            {children}
        </div>
    </div>
);

/**
 * Organizes a full experiment record into labeled, scannable sections
 * (submitter, research, composition, process, properties, images) instead of
 * one flat auto-generated key/value table - and only renders sections that
 * actually have data, so moderators aren't scanning past empty rows.
 */
const RecordDetailView = ({ experiment: exp }) => {
    const submitterFields = buildFields(exp.userInfo, [
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'orcid', label: 'ORCID' },
    ]);

    const researchFields = buildFields(exp.researchMetadata, [
        { key: 'publicationTitle', label: 'Publication Title' },
        {
            key: 'doi', label: 'DOI', formatter: (doi) => (
                <a href={`https://doi.org/${doi}`} target="_blank" rel="noopener noreferrer">{doi}</a>
            )
        },
        { key: 'deviceManufacturer', label: 'Device Manufacturer' },
        { key: 'deviceModel', label: 'Device Model' },
        { key: 'customDevice', label: 'Custom Device' },
    ]);
    const abstractText = exp.researchMetadata?.abstractText;

    const polymerComponents = exp.polymerProperty?.polymerComponents || [];
    const polymerRows = polymerComponents.map((c) => [
        c.newPolymer
            ? `${c.polymerName} (new${c.newPolymer.monomers?.length ? `: ${c.newPolymer.monomers.join('-')}` : ''}, ${c.pendingApproval ? 'awaiting approval' : 'not accepted'})`
            : c.polymerName || '-',
        c.composition?.length
            ? `${c.composition.map(f => f.monomer).join(':')} ${c.composition.map(f => f.fraction).join(':')} ${c.compositionUnit || ''}`
            : c.copolymer ? 'copolymer, not stated' : '-',
        withUnit(c.molecularWeight, c.molecularWeightUnit) || '-',
        withUnit(c.weightRatio, c.weightRatioUnit) || '-',
    ]);
    // A blend is two or more polymers; a copolymer is one polymer with a composition.
    const polymerKind = polymerComponents.length > 1 ? 'Blend'
        : polymerComponents[0]?.copolymer || polymerComponents[0]?.composition?.length ? 'Copolymer'
            : polymerComponents.length ? 'Homopolymer' : null;
    // How the solution(s) were prepared: a mix of separately prepared solutions, and/or one per needle channel
    const feedSections = (exp.solutions || []).map((feed) => {
        const mixed = (feed.stocks || []).length > 1;
        const channel = feed.channel && feed.channel !== 'MAIN' ? CHANNEL_LABELS[feed.channel] || feed.channel : null;
        const flowRate = channel && exp.processParameter
            ? withUnit(exp.processParameter[`flowRate${channel}`], exp.processParameter.flowRateUnit) : null;
        const p = feed.properties || {};
        const measured = [
            withUnit(p.viscosity, p.viscosityUnit), withUnit(p.surfaceTension, p.surfaceTensionUnit),
            withUnit(p.conductivity, p.conductivityUnit), withUnit(p.evaporationRate, p.evaporationRateUnit),
            p.PH !== null && p.PH !== undefined ? `pH ${p.PH}` : null,
        ].filter(Boolean).join(' · ');
        return {
            title: `${channel ? `${channel} solution` : 'Solution preparation'}${mixed ? ` · mixed from ${feed.stocks.length} solutions${feed.mixRatioUnit ? ` (${feed.mixRatioUnit})` : ''}` : ''}${flowRate ? ` · ${flowRate}` : ''}`,
            mixed,
            estimated: feed.effectiveValuesEstimated && !channel,
            measured,
            rows: (feed.stocks || []).map((st, i) => [
                ...(mixed ? [stockLabel(st, i)] : []),
                polymersText(st.polymers) || '-',
                solventsText(st.solvents) || '-',
                withUnit(st.concentration, st.concentrationUnit) || '-',
                ...(mixed ? [withUnit(st.share, feed.mixRatioUnit) || '-'] : []),
                additivesText(st.additives) || '-',
            ]),
        };
    });
    const additiveRows = (exp.additives || []).map((a) => [
        a.additiveName || '-',
        additiveAmountText(a) || '-',
    ]);

    const solventRows = (exp.solventProperty?.solventComponents || []).map((c) => [
        c.solventName || '-',
        withUnit(c.weight, c.weightUnit) || '-',
        withUnit(c.volumeRatio, c.volumeRatioUnit) || '-',
    ]);

    const solutionFields = buildFields(exp.solutionProperty, [
        { key: 'concentration', label: 'Concentration', unitKey: 'concentrationUnit' },
        { key: 'viscosity', label: 'Viscosity', unitKey: 'viscosityUnit' },
        { key: 'solutionViscosity', label: 'Solution Viscosity' },
        { key: 'surfaceTension', label: 'Surface Tension', unitKey: 'surfaceTensionUnit' },
        { key: 'conductivity', label: 'Conductivity', unitKey: 'conductivityUnit' },
        { key: 'solutionConductivity', label: 'Solution Conductivity' },
        { key: 'evaporationRate', label: 'Evaporation Rate', unitKey: 'evaporationRateUnit' },
        { key: 'pH', label: 'pH' },
    ]);

    const processFields = buildFields(exp.processParameter, [
        { key: 'voltage', label: 'Voltage', unitKey: 'voltageUnit' },
        { key: 'flowRate', label: exp.processParameter?.flowRateCore != null ? 'Flow Rate (total)' : 'Flow Rate', unitKey: 'flowRateUnit' },
        { key: 'flowRateCore', label: 'Flow Rate (core)', unitKey: 'flowRateUnit' },
        { key: 'flowRateMiddle', label: 'Flow Rate (middle)', unitKey: 'flowRateUnit' },
        { key: 'flowRateShell', label: 'Flow Rate (shell)', unitKey: 'flowRateUnit' },
        { key: 'tipCollectorDistance', label: 'Tip-Collector Distance', unitKey: 'tipCollectorDistanceUnit' },
        { key: 'spinningDuration', label: 'Spinning Duration', unitKey: 'spinningDurationUnit' },
    ]);
    const instabilityBadges = (exp.processParameter?.instability || [])
        .map((i) => (i.abbreviation ? `${i.label} (${i.abbreviation})` : i.label))
        .filter(Boolean);

    const needleFields = buildFields(exp.needleProperty, [{ key: 'needleType', label: 'Needle Type' }]);
    const needleDefinition = exp.needleProperty?.needleDefinition;

    const ambientFields = buildFields(exp.ambientParameter, [
        { key: 'temperature', label: 'Temperature', unitKey: 'temperatureUnit' },
        { key: 'humidity', label: 'Humidity', unitKey: 'humidityUnit' },
    ]);

    const collectorFields = buildFields(exp.collectorProperty, [{ key: 'collectorType', label: 'Collector Type' }]);
    const collectorDefinition = exp.collectorProperty?.collectorDefinition;

    const fiberFields = buildFields(exp.fiberProperty, [
        { key: 'isFormationStable', label: 'Formation Stable' },
        {
            key: 'fiberDiameter', label: 'Fiber Diameter', formatter: (d, fp) => {
                const unit = fp.fiberDiameterUnit;
                if (fp.fiberDiameterQualifier === 'RANGE' && fp.fiberDiameterMin != null && fp.fiberDiameterMax != null)
                    return `${withUnit(d, unit)} (midpoint of ${fp.fiberDiameterMin}–${withUnit(fp.fiberDiameterMax, unit)})`;
                if (fp.fiberDiameterQualifier === 'APPROXIMATE') return `≈ ${withUnit(d, unit)}`;
                return withUnit(d, unit);
            }
        },
        {
            key: 'diameterMethod', label: 'Diameter Measured By', formatter: (m, fp) =>
                fp.diameterNMeasured ? `${m}, n = ${fp.diameterNMeasured}` : m
        },
        { key: 'fiberDiameterVariation', label: 'Diameter Variation', unitKey: 'fiberDiameterVariationUnit' },
        { key: 'productWeight', label: 'Product Weight', unitKey: 'productWeightUnit' },
        { key: 'qualityGrade', label: 'Quality Grade' },
    ]);
    const morphologyBadges = (exp.fiberProperty?.fiberMorphology || [])
        .map((m) => (m.category ? `${m.category}: ${m.label}` : m.label))
        .filter(Boolean);

    const mechanicalFields = buildFields(exp.mechanicalProperty, [
        { key: 'tensileStrength', label: 'Tensile Strength', unitKey: 'tensileStrengthUnit' },
        { key: 'modulus', label: 'Modulus', unitKey: 'modulusUnit' },
        { key: 'elongationAtBreak', label: 'Elongation at Break', unitKey: 'elongationAtBreakUnit' },
        { key: 'fractureBehaviour', label: 'Fracture Behaviour' },
    ]);

    const functionalFields = buildFields(exp.functionalProperty, [
        { key: 'surfaceArea', label: 'Surface Area', unitKey: 'surfaceAreaUnit' },
        { key: 'porosity', label: 'Porosity', unitKey: 'porosityUnit' },
        { key: 'permeability', label: 'Permeability', unitKey: 'permeabilityUnit' },
        { key: 'wettability', label: 'Wettability', unitKey: 'wettabilityUnit' },
        { key: 'electricalConductivity', label: 'Electrical Conductivity', unitKey: 'electricalConductivityUnit' },
        { key: 'thermalConductivity', label: 'Thermal Conductivity', unitKey: 'thermalConductivityUnit' },
    ]);

    const applicationFields = buildFields(exp.applicationProperty, [
        { key: 'applicationType', label: 'Application Type' },
        { key: 'filtrationEfficiency', label: 'Filtration Efficiency', unitKey: 'filtrationEfficiencyUnit' },
        { key: 'drugReleaseKinetic', label: 'Drug Release Kinetic' },
        { key: 'cellAdhesion', label: 'Cell Adhesion' },
        { key: 'cellProliferation', label: 'Cell Proliferation' },
        { key: 'energyStorage', label: 'Energy Storage' },
    ]);

    const provenanceFields = buildFields(exp, [
        { key: 'dataOrigin', label: 'Data Origin', formatter: (v) => ({ LITERATURE: 'Literature', OWN_LAB: 'Own lab' }[v] || v) },
        { key: 'sourceLocation', label: 'Location in Source' },
        {
            key: 'extractionMethod', label: 'Values Obtained', formatter: (v) =>
                ({ REPORTED: 'Reported (text/table)', DIGITISED: 'Digitised from a figure', ESTIMATED: 'Estimated' }[v] || v)
        },
        {
            key: 'sourceRow', label: 'Bulk Upload', formatter: (row, e) =>
                `Row ${row}${e.importBatchId ? ` of upload #${e.importBatchId}` : ''}`
        },
    ]);

    const needleHasContent = needleFields.length > 0 || (needleDefinition && Object.keys(needleDefinition).length > 0);
    const collectorHasContent = collectorFields.length > 0 || (collectorDefinition && Object.keys(collectorDefinition).length > 0);

    return (
        <div className="record-detail-view">
            <div className="record-detail-header">
                <div className="record-detail-header-item">
                    <span className="record-detail-field-label">Record ID</span>
                    <span className="record-detail-field-value mono-font">#{exp.recordId}</span>
                </div>
                {exp.experimentId != null && (
                    <div className="record-detail-header-item">
                        <span className="record-detail-field-label">Public Experiment ID</span>
                        <span className="record-detail-field-value mono-font">#{exp.experimentId}</span>
                    </div>
                )}
                {exp.timestamp && (
                    <div className="record-detail-header-item">
                        <span className="record-detail-field-label">Submitted</span>
                        <span className="record-detail-field-value">{new Date(exp.timestamp).toLocaleString()}</span>
                    </div>
                )}
                {exp.completenessScore != null && (
                    <div className="record-detail-header-item" title="Share of the tracked fields that are filled (required fields count double)">
                        <span className="record-detail-field-label">Completeness</span>
                        <span className="record-detail-field-value">
                            {exp.completenessScore}%
                            <span className="d-inline-block align-middle ms-2 rounded" style={{ width: 60, height: 6, background: '#e2e8f0' }} aria-hidden="true">
                                <span className="d-block h-100 rounded" style={{ width: `${exp.completenessScore}%`, background: exp.completenessScore >= 75 ? '#16a34a' : exp.completenessScore >= 50 ? '#d97706' : '#dc2626' }} />
                            </span>
                        </span>
                    </div>
                )}
            </div>

            {(exp.reviewNote || exp.openIssues?.length > 0) && (
                <div className="alert alert-warning py-2 px-3 mb-3 small" role="note">
                    {exp.reviewNote && (
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                            <i className="bi bi-chat-left-text me-2" aria-hidden="true"></i>
                            <strong>Moderator's note{exp.reviewedAt ? ` (${new Date(exp.reviewedAt).toLocaleDateString()})` : ''}:</strong> {exp.reviewNote}
                        </div>
                    )}
                    {exp.openIssues?.length > 0 && (
                        <div className={exp.reviewNote ? 'mt-2' : undefined}>
                            <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                            <strong>Still to fix before this record can be approved:</strong>
                            <ul className="mb-0 mt-1 ps-4">{exp.openIssues.map(m => <li key={m}>{m}</li>)}</ul>
                        </div>
                    )}
                </div>
            )}

            <div className="record-detail-grid">
                {submitterFields.length > 0 && (
                    <Section icon="bi-person-badge" title="Submitter">
                        <FieldGrid fields={submitterFields} />
                    </Section>
                )}

                {(researchFields.length > 0 || abstractText) && (
                    <Section icon="bi-journal-text" title="Research">
                        <FieldGrid fields={researchFields} />
                        {abstractText && <p className="record-detail-abstract">{abstractText}</p>}
                    </Section>
                )}

                {provenanceFields.length > 0 && (
                    <Section icon="bi-signpost-split" title="Provenance">
                        <FieldGrid fields={provenanceFields} />
                    </Section>
                )}

                {feedSections.map((f) => (
                    <Section key={f.title} icon="bi-shuffle" title={f.title}>
                        <MiniTable
                            columns={[...(f.mixed ? ['Solution'] : []), 'Polymers', 'Solvents', 'Concentration', ...(f.mixed ? ['Share in mix'] : []), 'Additives']}
                            rows={f.rows} />
                        {f.measured && <div className="small text-muted mt-1">Measured: {f.measured}</div>}
                        {f.estimated && (
                            <div className="small text-muted mt-1">The overall concentration and blend shares below are estimates (a v/v mix of wt% solutions, assuming equal densities).</div>
                        )}
                    </Section>
                ))}

                {polymerRows.length > 0 && (
                    <Section icon="bi-diagram-3" title={`Polymers${polymerKind ? ` · ${polymerKind}` : ''}`}>
                        <MiniTable columns={['Polymer', 'Copolymer composition', 'Molecular Weight', 'Share in blend']} rows={polymerRows} />
                    </Section>
                )}

                {additiveRows.length > 0 && (
                    <Section icon="bi-capsule" title="Additives">
                        <MiniTable columns={['Additive', 'Amount']} rows={additiveRows} />
                    </Section>
                )}

                {solventRows.length > 0 && (
                    <Section icon="bi-droplet" title="Solvent Composition">
                        <MiniTable columns={['Solvent', 'Weight', 'Volume Ratio']} rows={solventRows} />
                    </Section>
                )}

                {solutionFields.length > 0 && (
                    <Section icon="bi-water" title="Solution Properties">
                        <FieldGrid fields={solutionFields} />
                    </Section>
                )}

                {(processFields.length > 0 || instabilityBadges.length > 0) && (
                    <Section icon="bi-lightning-charge" title="Process Parameters">
                        <FieldGrid fields={processFields} />
                        <BadgeList items={instabilityBadges} className="bg-warning-subtle text-dark border" />
                    </Section>
                )}

                {needleHasContent && (
                    <Section icon="bi-tornado" title="Needle">
                        <FieldGrid fields={needleFields} />
                        <DefinitionMap map={needleDefinition} />
                    </Section>
                )}

                {ambientFields.length > 0 && (
                    <Section icon="bi-thermometer-half" title="Ambient Conditions">
                        <FieldGrid fields={ambientFields} />
                    </Section>
                )}

                {collectorHasContent && (
                    <Section icon="bi-collection" title="Collector">
                        <FieldGrid fields={collectorFields} />
                        <DefinitionMap map={collectorDefinition} />
                    </Section>
                )}

                {(fiberFields.length > 0 || morphologyBadges.length > 0) && (
                    <Section icon="bi-bezier2" title="Fiber Properties">
                        <FieldGrid fields={fiberFields} />
                        <BadgeList items={morphologyBadges} className="bg-info-subtle text-dark border" />
                    </Section>
                )}

                {mechanicalFields.length > 0 && (
                    <Section icon="bi-tools" title="Mechanical Properties">
                        <FieldGrid fields={mechanicalFields} />
                    </Section>
                )}

                {functionalFields.length > 0 && (
                    <Section icon="bi-gear" title="Functional Properties">
                        <FieldGrid fields={functionalFields} />
                    </Section>
                )}

                {applicationFields.length > 0 && (
                    <Section icon="bi-clipboard2-pulse" title="Application">
                        <FieldGrid fields={applicationFields} />
                    </Section>
                )}
            </div>

            {exp.fiberImages && exp.fiberImages.length > 0 && (
                <div className="admin-card record-detail-card record-detail-images">
                    <ReviewImage images={exp.fiberImages} />
                </div>
            )}
        </div>
    );
};

export default RecordDetailView;
